import { yupResolver } from '@hookform/resolvers/yup'
import {
  ControlledDateOnlyPicker,
  ControlledSelect,
  ControlledTextField,
  stopPropagationSubmitHandler,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Grid } from '@mui/material'
import { EDisbursementTarget, getEPaymentMethodList } from '@src/types/constants'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormDialog from '../Common/Dialog/FormDialog'
import type { DisbursementSchemaType } from './loan-form-schema'
import { DisbursementSchema } from './loan-form-schema'

interface Props {
  payeeType: EDisbursementTarget
  open: boolean
  onClose: () => void
  onSubmit: (data: DisbursementSchemaType) => void
  defaultValues?: DisbursementSchemaType
}

const DisbursementDialog = ({ payeeType, open, onClose, onSubmit, defaultValues }: Props) => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, setValue } = useForm<DisbursementSchemaType>({
    resolver: yupResolver(DisbursementSchema),
    defaultValues: defaultValues ?? ({ ...DisbursementSchema.getDefault() } as unknown as DisbursementSchemaType),
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmitHandler = (data: DisbursementSchemaType) => {
    reset()
    onClose()
    data.lines = defaultValues?.lines ?? []
    onSubmit(data)
  }

  const paymentMethodOptions = useTranslatedOptionEnum('enum.EPaymentMethod', getEPaymentMethodList())
  const title = payeeType === EDisbursementTarget.Merchant ? t('loan.addMerchant') : t('loan.addBorrower')

  useEffect(() => {
    setValue('payeeType', payeeType)
  })

  return (
    <FormDialog
      title={title}
      open={open}
      onClose={handleClose}
      onConfirm={stopPropagationSubmitHandler(handleSubmit(onSubmitHandler))}
    >
      <Grid container rowGap={2} columnGap={1}>
        <Grid size={12}>
          <ControlledTextField
            control={control}
            name="payeeId"
            label={payeeType === EDisbursementTarget.Merchant ? t('loan.targetMerchant') : t('loan.targetBorrower')}
          />
        </Grid>
        <Grid size={12}>
          <ControlledSelect
            control={control}
            name="paymentMethod"
            label={t('loan.paymentMethod')}
            options={paymentMethodOptions}
          />
        </Grid>
        <Grid size={12}>
          <ControlledDateOnlyPicker control={control} name="paymentOn" label={t('loan.paymentOn')} />
        </Grid>
      </Grid>
    </FormDialog>
  )
}

export default DisbursementDialog
